// src/hooks/useServices.ts - Version multi-tenant
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Service } from '../types/entities';
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { usePermissions } from './usePermissions';

// ===== HOOKS POUR LES SERVICES =====
export const useServices = (filters?: { categoryId?: string }) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['business', 'services', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      let q = query(
        collection(db, COLLECTIONS.services), 
        where('businessId', '==', businessId),
        orderBy('name')
      );

      // Filtre par catégorie côté serveur (Firebase)
      if (filters?.categoryId) {
        q = query(q, where('categoryId', '==', filters.categoryId));
      }

      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useServices - Main query');
        throw error;
      });
      
      const services = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          businessId: data.businessId,
          name: data.name,
          basePrice: data.basePrice,
          categoryId: data.categoryId || data.category, // Support migration
          description: data.description || '',
          pricingMode: data.pricingMode || 'fixed',
          unitLabel: data.unitLabel,
          taxRateId: data.taxRateId,
          priceIncludesTax: data.priceIncludesTax,
          // ✅ NOUVEAU : Quota référent
          quotaPercentage: data.quotaPercentage,
          // ✅ NOUVEAU : Produits consommés
          consumedProducts: data.consumedProducts || undefined,
          stockConsumptionMode: data.stockConsumptionMode,
          manufacturingTemplate: data.manufacturingTemplate,
        };
      }) as Service[];
      
      return services;
    },
    enabled: !!currentBusiness?.id && (
      (permissions?.canViewServices ?? false) ||
      (permissions?.canViewPrestations ?? false) ||
      (permissions?.canViewDashboard ?? false) ||
      (permissions?.canViewAccounting ?? false) ||
      (permissions?.canViewCashReports ?? false)
    ),
  });
};

export const useAddService = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (service: Omit<Service, 'id' | 'businessId'>) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }
      
      const cleanedService = {
        ...service,
        businessId,
        description: service.description || '',
      };
      const payload = Object.fromEntries(
        Object.entries(cleanedService).filter(([, v]) => v !== undefined && v !== null)
      ) as typeof cleanedService;
      const docRef = await addDoc(collection(db, COLLECTIONS.services), payload);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', 'services', businessId] });
    },
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Service> & { id: string; consumedProducts?: Service['consumedProducts'] | null; manufacturingTemplate?: Service['manufacturingTemplate'] | null }) => {
      const cleanedData: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        if (key === 'businessId' || value === undefined) continue;
        if (value === null) {
          cleanedData[key] = deleteField();
        } else {
          cleanedData[key] = value;
        }
      }

      if ('description' in data && data.description === undefined) {
        cleanedData.description = '';
      }

      const docRef = doc(db, COLLECTIONS.services, id);
      await updateDoc(docRef, cleanedData as never);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', 'services', businessId] });
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, COLLECTIONS.services, id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', 'services', businessId] });
    },
  });
};